import * as React from "react";
import { Stack, Card, Box, Button } from "@mui/material";
import { DataGridPremium, GridColDef, GridSelectionModel } from "@mui/x-data-grid-premium";
import CclAlertSnackbar from "../../../components/common/cclAlertSnackbar";
import { AlertColor } from "@mui/material";
import {
    useGetSharedAdminGroupDetailsQuery,
    useCreateSharedGroupMutation,
    useUpdateSharedGroupMutation,
    useAddGroupMemberMutation,
} from "../../../services/cclTokenedAssessmentApi";
import CclDataGridToolbar from "../../../components/common/cclDataGridToolbar/cclDataGridToolbar";
import CclAddUpdateAdminGroupDrawer from "./cclAddUpdateAdminGroupDrawer";
import CclAddGroupMemberDrawer from "./cclAddGroupMemberDrawer";
import useLogAccessEvent from "../../../hooks/useLogAccessEvent";
import { useCreateNewEnterpriseUserMutation } from "../../../services/cclTokenedEnterpriseUserApi";
import {
    AssessmentAdminGroupDetail,
    CreateGroupRequest,
    UpdateGroupRequest,
} from "../../../services/types/assessmentAPITypes";
import {
    AccessEventAssessmentAdminGroupDetails,
    AccessEventAssessmentAdministratorAdded,
} from "../../../services/types/accessEventTypes";

export interface AdministratorGroupsCardProps {
    onClickGroup: (groupId: number) => void;
}

const AdministratorGroupsCard: React.FC<AdministratorGroupsCardProps> = (
    props: AdministratorGroupsCardProps,
) => {
    const [selectedRows, setSelectedRows] = React.useState<GridSelectionModel>([]);
    const [showDrawer, setShowDrawer] = React.useState<boolean>(false);
    const [showAddMemberDrawer, setShowAddMemberDrawer] = React.useState<boolean>(false);
    const [showSnackbar, setShowSnackbar] = React.useState<boolean>(false);
    const [snackbarMessage, setSnackbarMessage] = React.useState<string>("");
    const [snackbarSeverity, setSnackbarSeverity] = React.useState<AlertColor>("info");
    const [currentGroup, setCurrentGroup] = React.useState<AssessmentAdminGroupDetail | undefined>(
        undefined,
    );

    // Fetch groups from API
    const { data: groups = [], isLoading, isError, error } = useGetSharedAdminGroupDetailsQuery();
    const [createGroup, { isLoading: createInProgress }] = useCreateSharedGroupMutation();
    const [editGroup, { isLoading: editInProgress }] = useUpdateSharedGroupMutation();
    const [addMember, { isLoading: addMemberInProgress }] = useAddGroupMemberMutation();
    const [createUser, { isLoading: createUserInProgress }] = useCreateNewEnterpriseUserMutation();

    const { logEvent } = useLogAccessEvent();

    React.useEffect(() => {
        if (isError) {
            setSnackbarMessage(
                `Error loading groups: ${(error as any)?.data?.message || "Unknown error"}`,
            );
            setSnackbarSeverity("error");
            setShowSnackbar(true);
        }
    }, [isError, error]);

    const columns: GridColDef[] = [
        {
            field: "aagKey",
            headerName: "ID",
            width: 80,
            type: "string",
            align: "center",
            headerAlign: "left",
            renderCell: (params) => String(params.value).replace(/,/g, ""),
        },
        {
            field: "aagName",
            headerName: "Name",
            width: 250,
            renderCell: (params) => {
                return (
                    <Button
                        variant="text"
                        onClick={() => props.onClickGroup(params.row.aagKey)}
                        style={{ padding: 0, minWidth: "unset" }}
                    >
                        {params.value}
                    </Button>
                );
            },
        },
        {
            field: "memberCount",
            headerName: "Members",
            width: 100,
            type: "number",
            align: "center",
            headerAlign: "center",
        },
        {
            field: "createdByDateTime",
            headerName: "Created",
            width: 160,
            valueGetter: (params) => {
                const value = params.value;
                if (!value) return "";
                return new Date(value).toLocaleDateString();
            },
        },
        {
            field: "createdByUserName",
            headerName: "Created By",
            width: 250,
        },
        {
            field: "lastModifiedByDateTime",
            headerName: "Modified",
            width: 160,
            valueGetter: (params) => {
                const value = params.value;
                if (!value) return "";
                return new Date(value).toLocaleDateString();
            },
        },
        {
            field: "lastModifiedByUserName",
            headerName: "Modified By",
            width: 250,
        },
    ];

    function handleCreate(newGroup: CreateGroupRequest) {
        createGroup(newGroup)
            .unwrap()
            .then((newGroup: AssessmentAdminGroupDetail) => {
                const evtDetails: AccessEventAssessmentAdminGroupDetails = {
                    aagKey: newGroup.aagKey.toString(),
                    groupName: newGroup.aagName,
                };
                logEvent("AssessmentAdministratorGroupCreated", evtDetails);
                setSnackbarMessage("Administrator Group Created");
                setSnackbarSeverity("success");
                setShowSnackbar(true);
                setShowDrawer(false);
            })
            .catch((err) => {
                setSnackbarMessage(
                    `Error creating group: ${(err as any)?.data?.message || "Unknown error"}`,
                );
                setSnackbarSeverity("error");
                setShowSnackbar(true);
            });
    }

    function handleUpdate(updatedGroup: UpdateGroupRequest) {
        editGroup(updatedGroup)
            .unwrap()
            .then(() => {
                const evtDetails: AccessEventAssessmentAdminGroupDetails = {
                    aagKey: updatedGroup.aagKey.toString(),
                    groupName: updatedGroup.name,
                };
                logEvent("AssessmentAdministratorGroupEdited", evtDetails);
                setSnackbarMessage("Administrator Group Updated");
                setSnackbarSeverity("success");
                setShowSnackbar(true);
                setShowDrawer(false);
            })
            .catch((err) => {
                setSnackbarMessage("Failed Administrator Group Update");
                setSnackbarSeverity("error");
                setShowSnackbar(true);
            });
    }

    function addMemberToGroup(resolvedImKey: number, email: string) {
        if (!currentGroup) return;
        addMember({ aagKey: currentGroup.aagKey, imKey: resolvedImKey })
            .unwrap()
            .then((newMember) => {
                const evtData: AccessEventAssessmentAdministratorAdded = {
                    aagKey: currentGroup.aagKey.toString(),
                    groupName: currentGroup.aagName,
                    imKey: newMember.admin.imKey.toString(),
                    email,
                };
                logEvent("AssessmentAdministratorAdded", evtData);
                setSnackbarMessage("Group Member Added");
                setSnackbarSeverity("success");
                setShowSnackbar(true);
                setShowAddMemberDrawer(false);
            })
            .catch((err) => {
                setSnackbarMessage("Add Group Member Failed");
                setSnackbarSeverity("error");
                setShowSnackbar(true);
            });
    }

    function handleAddMember(email: string, firstName: string, lastName: string, imKey?: number) {
        if (!currentGroup) return;
        if (!imKey) {
            createUser({ email, firstName, lastName })
                .unwrap()
                .then((newUser) => addMemberToGroup(newUser.imKey, email))
                .catch((err) => {
                    setSnackbarMessage("Add Group Member Failed");
                    setSnackbarSeverity("error");
                    setShowSnackbar(true);
                });
        } else {
            addMemberToGroup(imKey, email);
        }
    }

    function handleButtonClick(btnId: string) {
        switch (btnId) {
            case "create-group":
                setCurrentGroup(undefined);
                setShowDrawer(true);
                break;

            case "edit-group":
                if (selectedRows.length !== 1) return;
                const editId = selectedRows[0] as number;
                const groupToEdit = groups.find((g) => g.aagKey === editId);
                if (!groupToEdit) return;
                setCurrentGroup(groupToEdit);
                setShowDrawer(true);
                break;

            case "add-member":
                if (selectedRows.length !== 1) return;
                const groupId = selectedRows[0] as number;
                const selectedGroup = groups.find((g) => g.aagKey === groupId);
                if (!selectedGroup) return;
                setCurrentGroup(selectedGroup);
                setShowAddMemberDrawer(true);
                break;
        }
    }

    return (
        <Box sx={{ width: "100%", height: "100%", minHeight: 0 }}>
            {showDrawer ? (
                <CclAddUpdateAdminGroupDrawer
                    open={true}
                    adminGroup={currentGroup}
                    onCreate={handleCreate}
                    onUpdate={handleUpdate}
                    onCancel={() => setShowDrawer(false)}
                    operationInProgress={createInProgress || editInProgress}
                />
            ) : null}
            {showAddMemberDrawer && currentGroup ? (
                <CclAddGroupMemberDrawer
                    open={true}
                    aagKey={currentGroup.aagKey}
                    groupName={currentGroup.aagName}
                    onSave={handleAddMember}
                    onCancel={() => setShowAddMemberDrawer(false)}
                    isLoading={addMemberInProgress || createUserInProgress}
                />
            ) : null}
            <Stack spacing={2} sx={{ height: "100%" }}>
                <Card sx={{ p: 2, height: "100%", display: "flex", flexDirection: "column" }}>
                    <Box sx={{ flex: 1, width: "100%", minHeight: 0 }}>
                        <DataGridPremium
                            rows={groups}
                            columns={columns}
                            components={{ Toolbar: CclDataGridToolbar }}
                            componentsProps={{
                                toolbar: {
                                    showSaveState: false,
                                    hideSettingsButtons: true,
                                    savestate: null,
                                    clearstate: null,
                                    miscButtons: [
                                        {
                                            id: "create-group",
                                            caption: "Create Group",
                                            label: "Create Group",
                                            disable: false,
                                            returnSelected: false,
                                            restricted: false,
                                            onClick: () => handleButtonClick("create-group"),
                                            loadingBtn: true,
                                            isLoading: createInProgress,
                                        },
                                        {
                                            id: "edit-group",
                                            caption: "Edit Group",
                                            label: "Edit Group",
                                            disable: selectedRows?.length !== 1,
                                            returnSelected: true,
                                            restricted: false,
                                            onClick: () => handleButtonClick("edit-group"),
                                            loadingBtn: true,
                                            isLoading: editInProgress,
                                        },
                                        {
                                            id: "add-member",
                                            caption: "Add Group Member",
                                            label: "Add Group Member",
                                            disable: selectedRows?.length !== 1,
                                            returnSelected: true,
                                            restricted: false,
                                            onClick: () => handleButtonClick("add-member"),
                                            loadingBtn: true,
                                            isLoading: addMemberInProgress || createUserInProgress,
                                        },
                                    ],
                                    btnClick: (buttonName: string) => handleButtonClick(buttonName),
                                },
                            }}
                            initialState={{
                                sorting: { sortModel: [{ field: "aagName", sort: "asc" }] },
                            }}
                            getRowId={(row) => row.aagKey}
                            checkboxSelection
                            onSelectionModelChange={(newSelection) => {
                                setSelectedRows(newSelection);
                            }}
                            selectionModel={selectedRows}
                            loading={isLoading}
                            autoHeight={false}
                            sx={{ height: "100%" }}
                        />
                    </Box>
                </Card>
            </Stack>
            {showSnackbar ? (
                <CclAlertSnackbar
                    open={true}
                    severity={snackbarSeverity}
                    message={snackbarMessage}
                    onClose={() => setShowSnackbar(false)}
                />
            ) : null}
        </Box>
    );
};

export default AdministratorGroupsCard;
